import React, { useState, forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { TodoEnums } from "models/enums";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { SnoozeFormProps, SnoozeData } from "models/propTypes/commonPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { DateConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import SelectionTiles from "./selection/selectionTiles";
import { useTodoService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, EnumHelper } from "utils/helpers";
import { addDays } from "date-fns";

const SnoozeForm: ForwardRefRenderFunction<FormHandles, SnoozeFormProps> = (props, ref) => {
  const todoService = useTodoService();
  React.useImperativeHandle(ref, () => ({
    submit: async () => {
      typeof props.entityId === "number"
        ? await todoService.snooze(props.entityId, props.type, (selectedItem.data as SnoozeData).snoozeUntil)
        : await todoService.snoozeByIdAsGuid(props.entityId, props.type, (selectedItem.data as SnoozeData).snoozeUntil);
      return props.entityId;
    },
  }));

  const onChange = (selectedItems: SelectionItem[]) => {
    setSelectedItem(selectedItems[0]);
  };

  const getDate = (days: number, hour: number) => {
    const date = addDays(new Date(), days);
    date.setHours(hour, 0, 0);
    return date;
  };

  const getDateString = (date: Date | number) => {
    return LocalizationHelperInstance.getFormattedDate(date, DateConstants.DateDayTimeConfig);
  };

  const getItem = (now: Date, index: number, type: TodoEnums.SnoozeType) => {
    let text: string;
    let daysToAdd: number;
    let hour: number;

    switch (type) {
      case TodoEnums.SnoozeType.TODAY: {
        if (now.getHours() > 17) return;

        text = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.LaterToday);
        daysToAdd = 0;
        hour = 18;
        break;
      }
      case TodoEnums.SnoozeType.TOMORROW:
        text = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tomorrow);
        daysToAdd = 1;
        hour = 8;
        break;
      case TodoEnums.SnoozeType.THIS_WEEK: {
        const day = now.getDay();
        if (day === 0 || day > 4) return;

        text = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.LaterThisWeek);
        daysToAdd = 3;
        hour = 8;
        break;
      }
      case TodoEnums.SnoozeType.WEEKEND: {
        let day = now.getDay();
        day = day === 0 ? 7 : day;

        text = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ThisWeekend);
        daysToAdd = 6 - day + (day < 6 ? 0 : 7);
        hour = 8;
        break;
      }
      case TodoEnums.SnoozeType.NEXT_WEEK:
        text = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NextWeek);
        daysToAdd = 7;
        hour = 8;
        break;
    }

    const snoozeUntil = getDate(daysToAdd, hour);

    return {
      id: index,
      value: text,
      icon: IconDefinitionConstants.WClock,
      description: getDateString(snoozeUntil),
      data: {
        snoozeUntil: new Date(snoozeUntil),
      } as SnoozeData,
    };
  };

  const snoozeItems = React.useMemo(() => {
    const now = new Date();
    return EnumHelper.getItems(TodoEnums.SnoozeType)
      .map((key, index) => {
        return getItem(now, index, TodoEnums.SnoozeType[key as keyof typeof TodoEnums.SnoozeType]);
      })
      .filter((i) => !!i) as SelectionItem[];
  }, []);

  const [selectedItem, setSelectedItem] = useState<SelectionItem>(snoozeItems[0]);

  React.useEffect(() => {
    props.onFormValidationChange && props.onFormValidationChange(selectedItem !== undefined);
  }, [selectedItem, props.onFormValidationChange]);

  return (
    <SelectionTiles
      title={""}
      items={snoozeItems}
      value={[selectedItem]}
      onChange={onChange}
      hideViewAll={true}
      icon={IconDefinitionConstants.WClock}
    />
  );
};

export default forwardRef(SnoozeForm);
